import React, { useState } from 'react';
import { useToastContext } from '../contexts/ToastContext';
import {
  useCategoriesWithSubcategories,
  useCreateCategorie,
  useUpdateCategorie,
  useDeleteCategorie,
  useCreateSousCategorie,
  useUpdateSousCategorie,
  useDeleteSousCategorie,
} from '../hooks/useCategories';
import { Plus, Edit2, Trash2, FolderPlus, Save, X } from 'lucide-react';
import Button from './Button';

const CategoriesManager: React.FC = () => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const { data: categories = [], isLoading } = useCategoriesWithSubcategories();
  const createCategorie = useCreateCategorie();
  const updateCategorie = useUpdateCategorie();
  const deleteCategorie = useDeleteCategorie();
  const createSousCategorie = useCreateSousCategorie();
  const updateSousCategorie = useUpdateSousCategorie();
  const deleteSousCategorie = useDeleteSousCategorie();

  const [editingCatId, setEditingCatId] = useState<number | null>(null);
  const [editingSubCatId, setEditingSubCatId] = useState<number | null>(null);
  const [newCatName, setNewCatName] = useState('');
  const [newSubCatName, setNewSubCatName] = useState('');
  const [addingSubCatTo, setAddingSubCatTo] = useState<number | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await createCategorie.mutateAsync({ nom: newCatName });
      setNewCatName('');
      setShowAddCategory(false);
    } catch {
      showError('Erreur', 'Impossible de créer la catégorie.');
    }
  };

  const handleDeleteCategory = async (id: number, nom: string) => {
    if (window.confirm(`Supprimer la catégorie "${nom}" ? Toutes les sous-catégories seront également supprimées.`)) {
      try {
        await deleteCategorie.mutateAsync(id);
      } catch (error: any) {
        showError('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  const handleCreateSousCategorie = async (categorieId: number) => {
    if (!newSubCatName.trim()) return;
    try {
      await createSousCategorie.mutateAsync({ categorieId, data: { nom: newSubCatName } });
      setNewSubCatName('');
      setAddingSubCatTo(null);
    } catch {
      showError('Erreur', 'Impossible de créer la sous-catégorie.');
    }
  };

  const handleDeleteSousCategorie = async (id: number, nom: string) => {
    if (window.confirm(`Supprimer la sous-catégorie "${nom}" ?`)) {
      try {
        await deleteSousCategorie.mutateAsync(id);
      } catch (error: any) {
        showError('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  if (isLoading) {
    return <div className="p-6 text-center text-gray-600">Chargement des catégories...</div>;
  }

  if (categories.length === 0 && !showAddCategory) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 text-center space-y-4">
        <FolderPlus className="w-12 h-12 mx-auto text-gray-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Aucune catégorie</h3>
          <p className="text-sm text-gray-600 mt-1">
            Créez votre première catégorie pour organiser vos objets d&apos;inventaire.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowAddCategory(true)}>
          Créer une catégorie
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Gestion des catégories</h3>
        {!showAddCategory && (
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddCategory(true)}
          >
            Nouvelle catégorie
          </Button>
        )}
      </div>

      {showAddCategory && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Nom de la nouvelle catégorie"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <Button
              variant="primary"
              size="sm"
              icon={<Save className="w-4 h-4" />}
              onClick={handleCreateCategory}
              disabled={!newCatName.trim()}
            >
              Créer
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<X className="w-4 h-4" />}
              onClick={() => {
                setShowAddCategory(false);
                setNewCatName('');
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {categories.map((cat) => (
          <div key={cat.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📂</span>
                {editingCatId === cat.id ? (
                  <input
                    type="text"
                    defaultValue={cat.nom}
                    onBlur={(e) => {
                      if (e.target.value !== cat.nom) {
                        updateCategorie.mutate({ id: cat.id, data: { nom: e.target.value } });
                      }
                      setEditingCatId(null);
                    }}
                    className="px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                ) : (
                  <h4 className="text-md font-semibold text-gray-900">{cat.nom}</h4>
                )}
                <span className="text-xs text-gray-500">
                  ({cat.sousCategories?.length || 0} sous-catégorie{(cat.sousCategories?.length || 0) > 1 ? 's' : ''})
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setEditingCatId(cat.id)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  title="Modifier"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteCategory(cat.id, cat.nom)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="ml-8 space-y-2">
              {cat.sousCategories?.map((subCat) => (
                <div key={subCat.id} className="flex items-center justify-between py-1 px-2 hover:bg-gray-50 rounded">
                  {editingSubCatId === subCat.id ? (
                    <input
                      type="text"
                      defaultValue={subCat.nom}
                      onBlur={(e) => {
                        if (e.target.value !== subCat.nom) {
                          updateSousCategorie.mutate({ id: subCat.id, data: { nom: e.target.value } });
                        }
                        setEditingSubCatId(null);
                      }}
                      className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    <span className="text-sm text-gray-700">{subCat.nom}</span>
                  )}
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => setEditingSubCatId(subCat.id)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Modifier"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSousCategorie(subCat.id, subCat.nom)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}

              {addingSubCatTo === cat.id ? (
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="text"
                    value={newSubCatName}
                    onChange={(e) => setNewSubCatName(e.target.value)}
                    placeholder="Nouvelle sous-catégorie"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => handleCreateSousCategorie(cat.id)}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Enregistrer"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAddingSubCatTo(null);
                      setNewSubCatName('');
                    }}
                    className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                    title="Annuler"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingSubCatTo(cat.id)}
                  className="mt-2 flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-3 h-3" />
                  <span>Ajouter une sous-catégorie</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoriesManager;
